import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, Calendar, Lock } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FileUpload from "@/components/FileUpload";
import api from "@/lib/api";
import useFormDraft from "@/lib/useFormDraft";
import { useUser } from "@/lib/userContext";
import { payWithRazorpay } from "@/lib/razorpay";

const SYMPTOMS = [
    "Low Reach",
    "Low Engagement",
    "Low Leads",
    "Weak Positioning",
    "Inconsistent Content",
    "Low Conversion",
    "Other",
];
const TEAM_SIZES = ["Solo", "2 – 10", "11 – 50", "51 – 200", "200+"];
const SLOTS = ["10:30", "12:00", "14:30", "16:00", "17:30"];

const INITIAL_DRAFT = {
    files: [],
    form: {
        patient_name: "",
        email: "",
        phone: "",
        brand_name: "",
        industry: "",
        team_size: "",
        website: "",
        social_links: "",
        symptoms: [],
        current_situation: "",
        growth_aspirations: "",
        appointment_date: "",
        appointment_slot: "",
        file_paths: [],
    },
};

const INITIAL = INITIAL_DRAFT.form;

function getNext14Days() {
    // Rolling window — always starts from tomorrow at page load.
    // Returns the next 21 days (3 weeks) so users have more booking flexibility.
    const out = [];
    const now = new Date();
    for (let i = 1; i <= 21; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);
        out.push(d);
    }
    return out;
}

function fmt(d) {
    return d.toISOString().slice(0, 10);
}

function Field({ label, children, span = "" }) {
    return (
        <div className={`flex flex-col ${span}`}>
            <span className="field-label">{label}</span>
            {children}
        </div>
    );
}

export default function Doctor() {
    const { user, signIn } = useUser();
    const [draft, setDraft, clearDraft] = useFormDraft("draft_doctor", INITIAL_DRAFT);
    const { form, files } = draft;
    const setForm = (next) => setDraft((d) => ({ ...d, form: typeof next === "function" ? next(d.form) : next }));
    const setFiles = (next) => setDraft((d) => ({ ...d, files: typeof next === "function" ? next(d.files) : next }));

    const [busy, setBusy] = useState(false);
    const [tracking, setTracking] = useState(null);
    const [paid, setPaid] = useState(false);
    const [consultations, setConsultations] = useState([]);
    const [selectedCode, setSelectedCode] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/consultations").then((r) => {
            setConsultations(r.data);
            const featured = r.data.find((c) => c.featured) || r.data[0];
            if (featured) setSelectedCode(featured.code);
        });
    }, []);

    const selected = consultations.find((c) => c.code === selectedCode) || consultations[0];
    const priceInr = selected ? selected.amount / 100 : 0;
    const priceLabel = priceInr ? `₹${priceInr.toLocaleString("en-IN")}` : "—";

    const dates = useMemo(() => getNext14Days(), []);
    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

    const toggleSymptom = (sym) => {
        setForm((s) => ({
            ...s,
            symptoms: s.symptoms.includes(sym) ? s.symptoms.filter((x) => x !== sym) : [...s.symptoms, sym],
        }));
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        const required = ["patient_name", "email", "phone", "brand_name", "industry", "team_size", "current_situation", "growth_aspirations", "appointment_date", "appointment_slot"];
        const missing = required.find((k) => !form[k]?.trim?.());
        if (missing) return toast.error("Please complete all required fields");
        if (form.symptoms.length === 0) return toast.error("Select at least one symptom");
        if (!selected) return toast.error("No active consultation offering");
        if (!user) {
            toast.message("Sign in to book", { description: "We'll bring you right back to this form." });
            signIn("/doctor");
            return;
        }
        setBusy(true);
        try {
            const payload = { ...form, file_paths: files.map((f) => f.id) };
            const { data } = await api.post("/applications/doctor", payload);
            setTracking(data.tracking_id);
            clearDraft();
            toast.success("Booking received. Complete payment to confirm.");
            payWithRazorpay({
                productCode: selected.code,
                trackingId: data.tracking_id,
                customer: { name: form.patient_name, email: form.email, phone: form.phone },
                onSuccess: () => {
                    setPaid(true);
                    toast.success("Payment confirmed!");
                },
                onFailure: () => {
                    toast.error("Payment not completed. You can pay later from your status page.");
                },
            });
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Could not submit booking");
        } finally {
            setBusy(false);
        }
    };

    if (tracking) {
        return (
            <main className="min-h-screen" data-testid="doctor-success">
                <Navbar />
                <section className="pt-40 pb-32">
                    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 border is-active mb-8">
                            <Check size={20} />
                        </div>
                        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter mb-6">
                            {paid ? "Diagnosis confirmed." : "Booking received."}
                        </h1>
                        <p className="text-white/65 text-lg mb-10 max-w-xl mx-auto">
                            {paid
                                ? "We will reach out 24 hours before your appointment with a pre-diagnosis brief."
                                : "Complete payment to confirm. You can also pay later from your status page."}
                        </p>
                        <div className="card-surface p-8 inline-block">
                            <div className="eyebrow mb-2">Your Case ID</div>
                            <div className="font-mono text-2xl text-saffron tracking-widest" data-testid="doctor-tracking-id">
                                {tracking}
                            </div>
                        </div>
                        <div className="mt-12 flex flex-wrap justify-center gap-3">
                            {!paid && selected && (
                                <button
                                    onClick={() =>
                                        payWithRazorpay({
                                            productCode: selected.code,
                                            trackingId: tracking,
                                            customer: { name: form.patient_name, email: form.email, phone: form.phone },
                                            onSuccess: () => setPaid(true),
                                        })
                                    }
                                    className="btn-primary"
                                    data-testid="doctor-pay-again"
                                >
                                    Complete Payment · {priceLabel}
                                </button>
                            )}
                            <button onClick={() => navigate(`/status/${tracking}`)} className="btn-secondary" data-testid="doctor-track-btn">
                                Track Case <ArrowUpRight size={16} />
                            </button>
                        </div>
                    </div>
                </section>
                <Footer />
            </main>
        );
    }

    return (
        <main className="min-h-screen" data-testid="doctor-page">
            <Navbar />
            <section className="pt-32 pb-16 lg:pt-44 lg:pb-20 border-b border-white/8">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-8">
                        <div className="eyebrow mb-5 text-saffron">Dr. Aangan Theory · Brand Diagnosis</div>
                        <h1 className="font-display text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
                            We diagnose. <br />
                            You <span className="text-saffron">grow</span>.
                        </h1>
                        <p className="text-white/65 text-lg max-w-2xl">
                            A 60-minute Brand Scan and Growth Assessment with our senior strategists. You walk away with a written Growth Prescription you can act on immediately.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="card-surface p-6" data-testid="doctor-price-card">
                            {selected ? (
                                <>
                                    <div className="eyebrow mb-3">{selected.name}</div>
                                    <div className="font-display text-4xl text-white mb-2" data-testid="doctor-price-amount">{priceLabel}</div>
                                    <div className="text-white/55 text-sm">{selected.description || `${selected.duration_min || 60}-min Brand Scan + written Growth Prescription`}</div>
                                </>
                            ) : (
                                <div className="text-white/45 text-sm">Loading offerings...</div>
                            )}
                            {consultations.length > 1 && (
                                <div className="mt-5 flex flex-col gap-2">
                                    <div className="eyebrow">Choose plan</div>
                                    {consultations.map((c) => (
                                        <button
                                            key={c.code}
                                            type="button"
                                            onClick={() => setSelectedCode(c.code)}
                                            className={`tag text-left ${selectedCode === c.code ? "is-active" : ""}`}
                                            data-testid={`doctor-plan-${c.code}`}
                                        >
                                            {c.name} · ₹{(c.amount / 100).toLocaleString("en-IN")}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <form onSubmit={submit} className="max-w-5xl mx-auto px-6 lg:px-10 space-y-10">
                    {/* Patient details */}
                    <div className="card-surface p-8 lg:p-12">
                        <div className="eyebrow mb-6 text-saffron">01 · Patient Details</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                            <Field label="Name">
                                <input className="field-input" value={form.patient_name} onChange={set("patient_name")} data-testid="dr-input-patient_name" />
                            </Field>
                            <Field label="Email">
                                <input type="email" className="field-input" value={form.email} onChange={set("email")} data-testid="dr-input-email" />
                            </Field>
                            <Field label="Phone">
                                <input className="field-input" value={form.phone} onChange={set("phone")} data-testid="dr-input-phone" />
                            </Field>
                        </div>
                    </div>

                    {/* Brand */}
                    <div className="card-surface p-8 lg:p-12">
                        <div className="eyebrow mb-6 text-saffron">02 · Brand Details</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                            <Field label="Brand Name">
                                <input className="field-input" value={form.brand_name} onChange={set("brand_name")} data-testid="dr-input-brand_name" />
                            </Field>
                            <Field label="Industry">
                                <input className="field-input" value={form.industry} onChange={set("industry")} data-testid="dr-input-industry" />
                            </Field>
                            <Field label="Website">
                                <input className="field-input" value={form.website} onChange={set("website")} placeholder="https://" data-testid="dr-input-website" />
                            </Field>
                            <Field label="Social Links">
                                <input className="field-input" value={form.social_links} onChange={set("social_links")} placeholder="Comma separated" data-testid="dr-input-social_links" />
                            </Field>
                            <Field label="Team Size" span="md:col-span-2">
                                <div className="flex flex-wrap gap-2">
                                    {TEAM_SIZES.map((t) => (
                                        <button key={t} type="button" onClick={() => setForm((s) => ({ ...s, team_size: t }))} className={`tag ${form.team_size === t ? "is-active" : ""}`} data-testid={`dr-team-${t}`}>
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                        </div>
                    </div>

                    {/* Symptoms */}
                    <div className="card-surface p-8 lg:p-12">
                        <div className="eyebrow mb-6 text-saffron">03 · Symptoms</div>
                        <p className="text-white/55 text-sm mb-6">Select all that you are experiencing.</p>
                        <div className="flex flex-wrap gap-2 mb-10">
                            {SYMPTOMS.map((s) => {
                                const on = form.symptoms.includes(s);
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => toggleSymptom(s)}
                                        className={`tag ${on ? "is-active" : ""}`}
                                        data-testid={`dr-symptom-${s.replace(/\s/g, "-")}`}
                                    >
                                        {s}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="space-y-8">
                            <Field label="Current Situation">
                                <textarea className="field-input" rows={4} value={form.current_situation} onChange={set("current_situation")} placeholder="What is happening today?" data-testid="dr-input-current_situation" />
                            </Field>
                            <Field label="Growth Aspirations">
                                <textarea className="field-input" rows={4} value={form.growth_aspirations} onChange={set("growth_aspirations")} placeholder="What does winning look like?" data-testid="dr-input-growth_aspirations" />
                            </Field>
                            <Field label="Brand assets (optional)">
                                <FileUpload category="doctor" value={files} onChange={setFiles} testid="dr-upload" />
                            </Field>
                        </div>
                    </div>

                    {/* Schedule */}
                    <div className="card-surface p-8 lg:p-12">
                        <div className="eyebrow mb-6 text-saffron">04 · Appointment</div>
                        <div className="mb-8">
                            <div className="field-label flex items-center gap-2">
                                <Calendar size={12} /> Pick a date
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">
                                {dates.map((d) => {
                                    const v = fmt(d);
                                    const on = form.appointment_date === v;
                                    return (
                                        <button
                                            type="button"
                                            key={v}
                                            onClick={() => setForm((s) => ({ ...s, appointment_date: v }))}
                                            className={`card-surface p-3 text-left transition-colors ${on ? "is-active" : ""}`}
                                            data-testid={`dr-date-${v}`}
                                        >
                                            <div className="font-mono text-xs text-white/45">{d.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase()}</div>
                                            <div className="font-display text-2xl mt-1">{d.getDate()}</div>
                                            <div className="text-xs text-white/55">{d.toLocaleDateString("en-GB", { month: "short" })}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <div className="field-label">Pick a slot (IST)</div>
                            <div className="flex flex-wrap gap-2">
                                {SLOTS.map((slot) => {
                                    const on = form.appointment_slot === slot;
                                    return (
                                        <button
                                            type="button"
                                            key={slot}
                                            onClick={() => setForm((s) => ({ ...s, appointment_slot: slot }))}
                                            className={`tag ${on ? "is-active" : ""}`}
                                            data-testid={`dr-slot-${slot}`}
                                        >
                                            {slot}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-6 flex-wrap">
                        <div className="text-white/55 text-sm flex items-center gap-2">
                            {!user && <Lock size={12} className="text-saffron-brand" />}
                            By submitting, you agree to a one-time payment of <span className="text-white">{priceLabel}</span> for the diagnosis.
                        </div>
                        <button type="submit" disabled={busy} className="btn-primary" data-testid="dr-submit-btn">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
                            {user ? `Book & Pay ${priceLabel}` : "Sign in to Book"}
                        </button>
                    </div>
                </form>
            </section>
            <Footer />
        </main>
    );
}

